// Parent explanation logic. Phase F3.
//
// Spec §"Parent explanation logic" (lines 1870–1971). Translates every
// engine decision into concrete, support-disclosed parent language.
// V1 explicitly requires specificity, transparency, and channel
// separation; the research upgrade requires support disclosure.
//
// Composition notes:
//   - `humanizeSkill` reads `label` from microSkills.json (§F3-Q1
//     resolution: option (d) — use the field Phase B already authored).
//     Falls back to ID-format humanization when the skill isn't in the
//     manifest (e.g. a synthetic ID in tests).
//   - Templates per spec lines 1925–1942 are reproduced verbatim. Drift
//     is guarded by the test-only ban list in the test file (§F3-Q2).
//   - All 6 decision types covered: advance, compactReview, review,
//     remediate, walkback, enrich.
//
// Consumed by Phase F2 adaptationDecisionEngine.ts via finalizeDecision.

import type {
  AdaptationDecisionType,
  DecisionBlockerCode,
  DecisionReasonCode,
  ParentExplanationPayload,
  StruggleKind,
  SupportPhraseKind,
} from "./types";
import microSkillsManifest from "../curriculum/data/manifests/microSkills.json" with { type: "json" };
import { inferChannel } from "./masteryClassInference";

interface ParentExplanationBuildInput {
  childId: string;
  skillId: string;
  decision: AdaptationDecisionType;
  supportLevelUsed: ParentExplanationPayload["supportLevelUsed"];
  reasonCodes: DecisionReasonCode[];
  blockerCodes: DecisionBlockerCode[];
  // MA-338: required (no default) — the caller must state whether the
  // evidence behind this decision includes a same-day attempt. Defaulting
  // this to `true` would silently reproduce the exact false-"Today" bug
  // this field exists to fix for any caller that forgets to pass it.
  hasSameDayAttempt: boolean;
  // ⭐MA-802: required (no default). True when any attempt in the window used read-aloud on a channel
  // where that is NOT support. Only changes the "independent" support sentence.
  readAloudHeard: boolean;
  // ⭐MA-816: copy-only. A correct post-IDK answer is in the window; with readAloudHeard it selects the combined opener.
  postIdkHelped?: boolean;
  // MA-630 §4e(b): required (no default, mirroring hasSameDayAttempt above)
  // — the caller must state whether the skill this decision is about has
  // reached durable mastery. Defaulting this to `false` would silently
  // reproduce the "partial progress on X" copy bug for a durably-mastered
  // skill with no next formal skill (priority 9 skips priority 8's
  // READY_FOR_ADVANCEMENT reason code entirely — see inferStrength below).
  // Copy-only: this field is read by inferStrength alone and never reaches
  // any decision, priority, reasonCode or the AdaptationDecision shape.
  durableMastery: boolean;
}

interface MicroSkillRecord {
  id: string;
  label: string;
}

// ── Skill label lookup ───────────────────────────────────────────────

const MICRO_SKILL_LABELS: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const entry of microSkillsManifest as MicroSkillRecord[]) {
    if (entry?.id && typeof entry.label === "string") {
      map[entry.id] = entry.label;
    }
  }
  return map;
})();

/** Resolve a skill ID to a human-readable label. Prefers the
 * `label` from microSkills.json (Phase B authored); falls back to an
 * ID-format humanization for IDs not in the manifest. §F3-Q1. */
export function humanizeSkill(skillId: string): string {
  const fromManifest = MICRO_SKILL_LABELS[skillId];
  if (fromManifest) return fromManifest;
  // Fallback: "ph.cvc.short_a" → "ph cvc short a" (best-effort; tests
  // assert this path only when the manifest doesn't carry the ID)
  return skillId.replace(/[._]/g, " ").trim();
}

// ── Support-phrase translation ───────────────────────────────────────

/** ⭐MA-1002 — the closed set of support sentences, in `SUPPORT_PHRASE_TEXT`
 * order. The parent card exposes the KIND (never the text) as
 * `data-support-phrase` so a live capture can say which sentence rendered. */
export const SUPPORT_PHRASE_KINDS = Object.freeze([
  "independent",
  "listened-read-aloud",
  "question-read-aloud",
  "read-aloud-access",
  "picture-support",
  "hint",
  "listened-read-aloud-hint",
  "question-read-aloud-hint",
  "adult",
  "mascot",
] as const satisfies readonly SupportPhraseKind[]);

/** ⭐MA-1002 — each kind's sentence, exactly as `supportPhraseForParent`
 * printed it before the kinds existed. */
export const SUPPORT_PHRASE_TEXT: Readonly<Record<SupportPhraseKind, string>> = Object.freeze({
  independent: "They worked independently.",
  "listened-read-aloud": "They listened to the text read aloud and answered on their own.",
  "question-read-aloud": "They had the question read aloud and answered on their own.",
  "read-aloud-access": "They used read-aloud support to access the words.",
  "picture-support": "They used picture or sound-box support during practice.",
  hint: "They used hint support during practice.",
  "listened-read-aloud-hint": "They listened to the text read aloud and used hint support during practice.",
  "question-read-aloud-hint": "They had the question read aloud and used hint support during practice.",
  adult: "An adult was present and helping during practice.",
  mascot: "The mascot walked them through the steps.",
});

/** Which support sentence the engine's supportLevelUsed (plus the MA-802
 * read-aloud channel and the MA-816 post-IDK flag) selects. Per spec lines
 * 1875–1882: success must be qualified by support level — independent vs
 * supported vs scaffolded. */
export function supportPhraseKindForParent(
  supportLevelUsed: ParentExplanationPayload["supportLevelUsed"],
  // ⭐MA-802: "listening" | "other" when read-aloud was heard on a non-support channel.
  readAloudHeard?: "listening" | "other" | false,
  postIdkHelped?: boolean,
): SupportPhraseKind {
  switch (supportLevelUsed) {
    case "independent":
      if (readAloudHeard === "listening") return "listened-read-aloud";
      if (readAloudHeard === "other") return "question-read-aloud";
      return "independent";
    case "audio-supported":
      return "read-aloud-access";
    case "visual-supported":
      return "picture-support";
    case "scaffolded":
      // ⭐MA-816 (RED SHOULD-1): keep the MA-802 read-aloud disclosure when the
      // window also heard read-aloud on a non-support channel; never claim
      // "on their own" beside hint support.
      if (postIdkHelped && readAloudHeard === "listening") return "listened-read-aloud-hint";
      if (postIdkHelped && readAloudHeard === "other") return "question-read-aloud-hint";
      return "hint";
    case "adult-supported":
      return "adult";
    case "mascot-guided":
      return "mascot";
  }
}

/** Translate the engine's supportLevelUsed enum into a sentence-form
 * disclosure phrase — the text of `supportPhraseKindForParent`'s kind. */
export function supportPhraseForParent(
  supportLevelUsed: ParentExplanationPayload["supportLevelUsed"],
  readAloudHeard?: "listening" | "other" | false,
  postIdkHelped?: boolean,
): string {
  return SUPPORT_PHRASE_TEXT[supportPhraseKindForParent(supportLevelUsed, readAloudHeard, postIdkHelped)];
}

// ── Evidence-date check (MA-338) ─────────────────────────────────────

/** True when at least one attempt's timestamp falls within the CURRENT
 * child-local calendar day (device clock). `useLatestDecision` recomputes
 * decisions at VIEW time over the recent attempt window, so a parent
 * opening the dashboard on a no-practice day must not read a false
 * "Today" — this is the same local-day boundary convention as
 * `todaysMidnightMs`/`filterAttemptsToday` (`src/lib/engine/trailState.ts`),
 * reproduced here (not imported) to keep this render-layer module
 * independent of the trail/reward engine. Test-injectable via `now`. */
export function hasSameDayEvidence(
  attempts: readonly { timestamp: string }[],
  now: Date = new Date(),
): boolean {
  const midnight = new Date(now.getTime());
  midnight.setHours(0, 0, 0, 0);
  const midnightMs = midnight.getTime();
  return attempts.some((a) => {
    const t = Date.parse(a.timestamp);
    return Number.isFinite(t) && t >= midnightMs;
  });
}

// ── Reason/blocker → narrative inference ─────────────────────────────

/** Infer a strength phrase from reason codes. Each reason code maps to
 * a concrete behavioural observation, not vague praise. Falls back to
 * a safe baseline if no reason code matches. */
export function inferStrength(
  reasonCodes: DecisionReasonCode[],
  practicedSkillLabel: string,
  // MA-630 §4e(b): the copy-only durableMastery signal, threaded from
  // finalizeDecision's masteryState (never a default — see the "required"
  // comment on ParentExplanationBuildInput.durableMastery above).
  durableMastery: boolean,
): string {
  // Prefer the strongest signals first
  if (reasonCodes.includes("READY_FOR_ADVANCEMENT")) {
    return `durable accuracy on ${practicedSkillLabel}`;
  }
  if (reasonCodes.includes("READY_FOR_COMPACT_REVIEW")) {
    return `durable accuracy on ${practicedSkillLabel} with one checkpoint remaining`;
  }
  if (reasonCodes.includes("DIRECT_ACCURACY_STRONG")) {
    return `accurate work on ${practicedSkillLabel}`;
  }
  if (reasonCodes.includes("TRANSFER_PASSED")) {
    return `transfer of ${practicedSkillLabel} into a new format`;
  }
  if (reasonCodes.includes("DELAYED_REVIEW_PASSED")) {
    return `retention of ${practicedSkillLabel} across days`;
  }
  // ⭐MA-630 §4e(b) — REVIEW FIX (RED BLOCK-1): the branch below is
  // deliberately gated on `reasonCodes.includes("PARTIAL_PROGRESS_OBSERVED")`
  // as well as `durableMastery`, NOT on `durableMastery` alone. `durableMastery`
  // is threaded into all 11 `finalizeDecision` call sites, and
  // `durableMastery ⟹ delayedReviewEvidence.passed` only rules out priorities
  // 5 and 6 — it does NOT rule out priorities 1, 2, 3, 4 or 7, every one of
  // which can fire with `durableMastery: true` and none of the five reason
  // codes above present. An earlier version of this fix checked `durableMastery`
  // alone and changed rendered copy at 4 more priorities repo-wide (not just
  // the seven MA-630 skills) — reproduced on `ms.math.11` (`mathConceptMastery`,
  // untouched by this PR) at priority 4: a picture-supported child's copy went
  // from "showed engagement with … success has so far come with picture or
  // sound-box support that named the answer" to "showed durable accuracy on …
  // […] that named the answer" — a self-contradicting sentence, and a
  // regression this PR must not ship. Scoping to PARTIAL_PROGRESS_OBSERVED
  // confines the branch to exactly the shape it was built for: priority 9's
  // literal `["PARTIAL_PROGRESS_OBSERVED"]` reasonCodes for a skill with
  // `unlocks: []` (`ms.math.19`/`.25`/`.27` once masterable — `durableMastery:
  // true` with `nextFormalSkillId: null` skips priority 8 entirely, per
  // adaptationDecisionEngine.ts). Every OTHER priority's rendered copy is
  // byte-identical to before this fix, by construction: this branch cannot
  // fire unless PARTIAL_PROGRESS_OBSERVED is literally in reasonCodes, which
  // only priority 9 ever emits.
  if (durableMastery && reasonCodes.includes("PARTIAL_PROGRESS_OBSERVED")) {
    return `durable accuracy on ${practicedSkillLabel}`;
  }
  if (reasonCodes.includes("PARTIAL_PROGRESS_OBSERVED")) {
    return `partial progress on ${practicedSkillLabel}`;
  }
  return `engagement with ${practicedSkillLabel}`;
}

/** ⭐MA-1002 — the closed set of struggle clauses `inferStruggle` can print,
 * in branch order, plus "none" (its empty return). The parent card exposes
 * the KIND (never the text) as `data-struggle-kind`. */
export const STRUGGLE_KINDS = Object.freeze([
  "same-error-loop",
  "frustration",
  "word-problem-transfer",
  "connected-text-transfer",
  "delayed-review",
  "high-hint-use",
  "strongest-hint",
  "read-aloud-only",
  "oral-only",
  "content-held",
  "picture-support-retest",
  "post-idk-retest",
  "support-retest",
  "none",
] as const satisfies readonly StruggleKind[]);

/** ⭐MA-1002 — each kind's clause, exactly as `inferStruggle` printed it
 * before the kinds existed ("none" → ""). */
export const STRUGGLE_TEXT: Readonly<Record<StruggleKind, (practicedSkillLabel: string) => string>> = Object.freeze({
  "same-error-loop": (practicedSkillLabel) => `the same error repeated across attempts on ${practicedSkillLabel}`,
  "frustration": (practicedSkillLabel) => `frustration during ${practicedSkillLabel} practice`,
  "word-problem-transfer": () => `the same math idea has not yet held in a word problem`,
  "connected-text-transfer": () => `the same idea has not yet held in connected text or a new format`,
  "delayed-review": (practicedSkillLabel) => `${practicedSkillLabel} has not yet been confirmed on a later review`,
  "high-hint-use": (practicedSkillLabel) => `several attempts on ${practicedSkillLabel} needed hint support`,
  "strongest-hint": (practicedSkillLabel) => `the strongest hint level was used during ${practicedSkillLabel} practice`,
  "read-aloud-only": (practicedSkillLabel) => `${practicedSkillLabel} success has only been seen with read-aloud support so far`,
  "oral-only": (practicedSkillLabel) => `${practicedSkillLabel} has been shown orally but not yet in independent reading`,
  "content-held": () => `the next planned content is being held back pending review`,
  "picture-support-retest": (practicedSkillLabel) => `${practicedSkillLabel} success has so far come with picture or sound-box support that named the answer`,
  "post-idk-retest": (practicedSkillLabel) => `a recent right answer on ${practicedSkillLabel} came after a hint was shown`,
  "support-retest": (practicedSkillLabel) => `${practicedSkillLabel} success was seen with support and needs an independent retest`,
  none: () => "",
});

/** Infer which struggle clause the reason + blocker codes select. Maps the
 * engine's diagnostic signals into a concrete struggle kind; "none" when
 * there is genuinely no struggle to report (decision-template handling uses
 * {struggle} only where relevant — advance/compactReview templates omit it). */
export function inferStruggleKind(
  reasonCodes: DecisionReasonCode[],
  blockerCodes: DecisionBlockerCode[],
): StruggleKind {
  if (
    reasonCodes.includes("SAME_ERROR_LOOP_DETECTED") ||
    blockerCodes.includes("BLOCK_SAME_ERROR_LOOP")
  ) {
    return "same-error-loop";
  }
  if (reasonCodes.includes("HIGH_FRUSTRATION_DETECTED")) {
    return "frustration";
  }
  // WORD_PROBLEM_TRANSFER_REQUIRED check must run BEFORE the
  // CONNECTED_TEXT / BLOCK_NO_TRANSFER catch — the decision engine sets
  // BLOCK_NO_TRANSFER on BOTH the connected-text AND word-problem
  // paths, so the more-specific reason code needs to win first.
  // Caught by Phase F4 integration test I8 (word-problem transfer
  // required, math).
  if (reasonCodes.includes("WORD_PROBLEM_TRANSFER_REQUIRED")) {
    return "word-problem-transfer";
  }
  if (
    reasonCodes.includes("CONNECTED_TEXT_REQUIRED") ||
    blockerCodes.includes("BLOCK_NO_TRANSFER")
  ) {
    return "connected-text-transfer";
  }
  if (
    reasonCodes.includes("DELAYED_REVIEW_REQUIRED") ||
    blockerCodes.includes("BLOCK_NO_DELAYED_REVIEW")
  ) {
    return "delayed-review";
  }
  if (
    reasonCodes.includes("HIGH_HINT_USAGE") ||
    blockerCodes.includes("BLOCK_HIGH_HINT_USE")
  ) {
    return "high-hint-use";
  }
  if (reasonCodes.includes("H4_USED") || blockerCodes.includes("BLOCK_H4_SUCCESS")) {
    return "strongest-hint";
  }
  if (
    reasonCodes.includes("AUDIO_SUPPORTED_ONLY") ||
    blockerCodes.includes("BLOCK_AUDIO_SUPPORTED_READING")
  ) {
    return "read-aloud-only";
  }
  if (
    reasonCodes.includes("ORAL_ONLY_EVIDENCE") ||
    blockerCodes.includes("BLOCK_ORAL_VOCAB_FOR_READING")
  ) {
    return "oral-only";
  }
  if (
    reasonCodes.includes("CONTENT_EXPERT_REVIEW_NEEDED") ||
    blockerCodes.includes("BLOCK_EXPERT_REVIEW_NEEDED") ||
    reasonCodes.includes("CONTENT_EXCLUDED_FROM_INDEPENDENT_PRACTICE") ||
    blockerCodes.includes("BLOCK_CONTENT_NOT_LAUNCH_SAFE")
  ) {
    return "content-held";
  }
  // MA-485 slice b: name the picture/post-IDK case specifically — a
  // support that visually named the answer is a materially different,
  // more honest thing to tell a parent than the generic fallback below.
  // Checked before the generic branch; PICTURE_SUPPORT_RETEST never fires
  // without SUPPORT_RETEST_REQUIRED also present (adaptationDecisionEngine
  // priority 4), so this ordering just picks the more specific phrase.
  if (reasonCodes.includes("PICTURE_SUPPORT_RETEST")) {
    return "picture-support-retest";
  }
  // ⭐MA-816: a correct answer carrying `postIdkSupported` means an "I don't know"
  // press brought up a HINT (MA-483 rung) and the child then answered; the answer
  // itself is never shown on a correct row. No picture or sound box was shown.
  if (reasonCodes.includes("POST_IDK_RETEST")) {
    return "post-idk-retest";
  }
  if (reasonCodes.includes("SUPPORT_RETEST_REQUIRED")) {
    return "support-retest";
  }
  return "none";
}

/** Infer a struggle phrase from reason + blocker codes: the text of
 * `inferStruggleKind`'s kind. Returns an empty string when there is
 * genuinely no struggle to report. */
export function inferStruggle(
  reasonCodes: DecisionReasonCode[],
  blockerCodes: DecisionBlockerCode[],
  practicedSkillLabel: string,
): string {
  return STRUGGLE_TEXT[inferStruggleKind(reasonCodes, blockerCodes)](practicedSkillLabel);
}

/** Build the concrete next-step phrase that goes into the
 * decision-specific template's `{nextStep}` slot. */
export function inferNextStep(
  decision: AdaptationDecisionType,
  practicedSkillLabel: string,
): string {
  switch (decision) {
    case "advance":
      return "moving to the next skill in the progression";
    case "compactReview":
      return `starting the next skill while keeping one short checkpoint on ${practicedSkillLabel}`;
    case "review":
      return `another short practice on ${practicedSkillLabel} to confirm the skill`;
    case "remediate":
      return `a short targeted reteach on ${practicedSkillLabel}`;
    case "walkback":
      return `stepping back to a prerequisite skill before returning to ${practicedSkillLabel}`;
    case "enrich":
      return `an age-appropriate challenge inside ${practicedSkillLabel}`;
  }
}

/** Return the decision-specific narrative template. Slot tokens:
 *   {skill} — practicedSkillLabel
 *   {strength} — strengthObserved
 *   {struggle} — struggleObserved
 *   {support} — supportPhraseForParent output
 *   {nextStep} — inferNextStep output
 * Per spec lines 1925–1942, with the MA-338 date-aware opener: the
 * literal "Today" claim is only truthful when `hasSameDayAttempt` is
 * true (see `hasSameDayEvidence` above); otherwise the opener reads
 * "In recent practice," — every other word is unchanged.
 *
 * ⭐MA-630 §4e(a) — `review`/`remediate`/`walkback` each select a
 * STRUGGLE-FREE variant when `struggleObserved === ""`, dropping the
 * "noticed <empty>" clause entirely instead of interpolating an empty
 * string into it. This is a LIVE bug on origin/main, not prophylactic:
 * `inferStruggle` legitimately returns `""` in three reachable shapes
 * (priority 9 `review` with only PARTIAL_PROGRESS_OBSERVED; priority 10
 * `walkback`/`remediate` with only NEEDS_WALKBACK/NEEDS_REMEDIATION), and
 * two of those three already ship today at priority 10 on ANY skill —
 * proven end-to-end on `ms.math.11`. Blast radius is exactly these three
 * shapes: in every other branch `struggleObserved` is non-empty, so the
 * variant never fires and every other rendered string is byte-identical
 * to before. Fixed HERE, not in `inferStruggle` — a fallback string there
 * would invent a struggle claim for a child who genuinely has none. */
export function templateForDecision(
  decision: AdaptationDecisionType,
  hasSameDayAttempt: boolean,
  struggleObserved: string,
): string {
  const opener = hasSameDayAttempt
    ? "Today your child showed {strength}."
    : "In recent practice, your child showed {strength}.";
  const hasStruggle = struggleObserved !== "";
  switch (decision) {
    case "advance":
      return `${opener} {support} PencilWise will now move to the next skill: {nextStep}.`;
    case "compactReview":
      return `${opener} {support} PencilWise will start the next skill while keeping one short checkpoint on {skill}: {nextStep}.`;
    case "review":
      return hasStruggle
        ? `${opener} {support} PencilWise also noticed {struggle}, so the next step is {nextStep}.`
        : `${opener} {support} The next step is {nextStep}.`;
    case "remediate":
      return hasStruggle
        ? `${opener} {support} PencilWise noticed a repeatable pattern: {struggle}. The next step is a short targeted reteach: {nextStep}.`
        : `${opener} {support} The next step is a short targeted reteach: {nextStep}.`;
    case "walkback":
      return hasStruggle
        ? `${opener} {support} PencilWise noticed that an earlier foundation still needs work: {struggle}. The next step is to step back briefly and rebuild that piece: {nextStep}.`
        : `${opener} {support} The next step is to step back briefly and rebuild that piece: {nextStep}.`;
    case "enrich":
      return `${opener} {support} Rather than moving to a skill that is too early right now, PencilWise will add an age-appropriate challenge inside {skill}: {nextStep}.`;
  }
}

// ── Public surface ───────────────────────────────────────────────────

/** Build the full parent-facing payload for an engine decision. The
 * payload disclosures (handwriting/numeral channel separation) are
 * always true per V1 spec — these are surface-level reminders the
 * parent UI uses to render channel labels, not engine state. */
export function buildParentExplanation(
  input: ParentExplanationBuildInput,
): ParentExplanationPayload {
  const practicedSkillLabel = humanizeSkill(input.skillId);
  // ⭐MA-1002: the kind is computed once and the sentence is its text, so
  // the card's data-support-phrase can never disagree with the sentence.
  const supportPhraseKind = supportPhraseKindForParent(
    input.supportLevelUsed,
    input.readAloudHeard
      ? inferChannel(input.skillId) === "listeningComprehensionMastery"
        ? "listening"
        : "other"
      : false,
    input.postIdkHelped === true,
  );
  const supportPhrase = SUPPORT_PHRASE_TEXT[supportPhraseKind];

  const strengthObserved = inferStrength(
    input.reasonCodes,
    practicedSkillLabel,
    input.durableMastery,
  );
  const struggleKind = inferStruggleKind(input.reasonCodes, input.blockerCodes);
  const struggleObserved = STRUGGLE_TEXT[struggleKind](practicedSkillLabel);
  const nextStep = inferNextStep(input.decision, practicedSkillLabel);

  const summaryText = templateForDecision(
    input.decision,
    input.hasSameDayAttempt,
    struggleObserved,
  )
    .replace("{skill}", practicedSkillLabel)
    .replace("{strength}", strengthObserved)
    .replace("{struggle}", struggleObserved)
    .replace("{support}", supportPhrase)
    .replace("{nextStep}", nextStep);

  return {
    childId: input.childId,
    skillId: input.skillId,
    decision: input.decision,
    practicedSkillLabel,
    strengthObserved,
    struggleObserved,
    supportLevelUsed: input.supportLevelUsed,
    successQualification:
      input.supportLevelUsed === "independent"
        ? "independent"
        : input.supportLevelUsed === "audio-supported" ||
            input.supportLevelUsed === "visual-supported" ||
            input.supportLevelUsed === "scaffolded" ||
            input.supportLevelUsed === "adult-supported" ||
            input.supportLevelUsed === "mascot-guided"
          ? "supported"
          : "readiness_only",
    nextStep,
    readinessMessage:
      input.decision === "advance" || input.decision === "compactReview"
        ? "ready_for_challenge"
        : input.decision === "review"
          ? "needs_review"
          : input.decision === "remediate"
            ? "needs_remediation"
            : input.decision === "walkback"
              ? "needs_walkback"
              : "ready_for_enrichment",
    discloseHandwritingSeparation: true,
    discloseNumeralSeparation: true,
    hasSameDayAttempt: input.hasSameDayAttempt,
    supportPhraseKind,
    struggleKind,
    summaryText,
  };
}
